import React, { createContext, useContext, useState, useEffect } from 'react';
import apiService from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in from localStorage and validate token
    const checkAuthStatus = async () => {
      try {
        const token = localStorage.getItem('authToken');
        const storedUser = localStorage.getItem('adminUser');
        console.log('Checking auth status, token exists:', !!token, 'stored user exists:', !!storedUser);
        
        if (token) {
          try {
            // Try to get current user info to validate token
            const userData = await apiService.getCurrentUser();
            console.log('User data from API:', userData);
            
            // Transform the user data to match frontend format
            const userInfo = {
              id: userData.user.id,
              fullName: userData.user.name,
              name: userData.user.name,
              employeeId: userData.user.employee_id,
              role: userData.user.isAdmin ? 'admin' : 'user',
              isAdmin: userData.user.isAdmin,
              status: userData.user.isActive ? 'active' : 'inactive',
              avatar: '👨‍💼'
            };
            
            console.log('Transformed user info:', userInfo);
            setUser(userInfo);
            localStorage.setItem('adminUser', JSON.stringify(userInfo));
          } catch (apiError) {
            console.error('API call failed, trying fallback:', apiError);
            
            // Fallback: use stored user data if available
            if (storedUser) {
              try {
                const fallbackUser = JSON.parse(storedUser);
                console.log('Using fallback user data:', fallbackUser);
                setUser(fallbackUser);
              } catch (parseError) {
                console.error('Failed to parse stored user data:', parseError);
                // Clear invalid data
                apiService.logout();
                setUser(null);
                localStorage.removeItem('adminUser');
              }
            } else {
              // No stored data, clear everything
              apiService.logout();
              setUser(null);
              localStorage.removeItem('adminUser');
    }
          }
        } else {
          console.log('No token found in localStorage');
          // Clear any stale data
          setUser(null);
          localStorage.removeItem('adminUser');
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        // Clear invalid token
        apiService.logout();
        setUser(null);
        localStorage.removeItem('adminUser');
      } finally {
    setLoading(false);
      }
    };

    checkAuthStatus();
  }, []);

  const login = async (employeeId, password) => {
    try {
      const response = await apiService.login(employeeId, password);

      // Get user details after successful login
      const userData = await apiService.getCurrentUser();

      const userInfo = {
        id: userData.user.id,
        fullName: userData.user.name,
        name: userData.user.name,
        employeeId: userData.user.employee_id,
        role: userData.user.isAdmin ? 'admin' : 'user',
        isAdmin: userData.user.isAdmin,
        status: userData.user.isActive ? 'active' : 'inactive',
        avatar: '👨‍💼'
      };

      setUser(userInfo);
      localStorage.setItem('adminUser', JSON.stringify(userInfo));

      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        error: error.message || 'Invalid employee ID or password'
      };
    }
  };

  const register = async (name, employeeId, password) => {
    try {
      await apiService.register(name, employeeId, password);

      // Registration successful, but don't auto-login
      // User should manually log in after registration
      return { success: true };
    } catch (error) {
      console.error('Registration error:', error);
      return {
        success: false,
        error: error.message || 'Registration failed'
      };
    }
  };

  const logout = () => {
    setUser(null);
    apiService.logout();
    localStorage.removeItem('adminUser');
  };

  const value = {
    user,
    login,
    register,
    logout,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext; 